import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Loader2, Bot, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';

interface LogCustomWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultActivityName?: string;
}

interface CompletedWorkout {
  activityName: string;
  duration: number;
  caloriesBurned?: number;
  difficulty: string;
  notes?: string;
}

export const LogCustomWorkoutModal = ({ isOpen, onClose, onSuccess, defaultActivityName }: LogCustomWorkoutModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const coachName = profile?.ai_coach_name || 'Arnold';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activityName, setActivityName] = useState(defaultActivityName || '');
  const [duration, setDuration] = useState('');
  const [caloriesBurned, setCaloriesBurned] = useState('');
  const [notes, setNotes] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [showAnalyzePopup, setShowAnalyzePopup] = useState(false);
  const [completedWorkout, setCompletedWorkout] = useState<CompletedWorkout | null>(null);

  useEffect(() => {
    if (isOpen) setActivityName(defaultActivityName || '');
  }, [isOpen, defaultActivityName]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error(t('components.logCustomWorkout.toast.mustLogin'));
      return;
    }

    if (!activityName.trim() || !duration) {
      toast.error(t('components.logCustomWorkout.toast.fillNameDuration'));
      return;
    }

    setLoading(true);

    try {
      // Create the workout record
      const { data: workout, error: workoutError } = await supabase
        .from('workouts')
        .insert({
          user_id: user.id,
          title: activityName.trim(),
          workout_type: 'custom',
          duration_minutes: parseInt(duration),
          calories_burned: caloriesBurned ? parseInt(caloriesBurned) : null,
          difficulty_level: difficulty,
          completed_at: new Date().toISOString(),
          notes: notes.trim() || null,
        })
        .select()
        .single();

      if (workoutError) throw workoutError;

      toast.success(t('components.logCustomWorkout.toast.success'));
      
      // Store completed workout data for Arnold analysis
      setCompletedWorkout({
        activityName: activityName.trim(),
        duration: parseInt(duration),
        caloriesBurned: caloriesBurned ? parseInt(caloriesBurned) : undefined,
        difficulty,
        notes: notes.trim() || undefined,
      });
      
      // Reset form
      setActivityName('');
      setDuration('');
      setCaloriesBurned('');
      setNotes('');
      setDifficulty('medium');
      
      // Show analyze popup - DON'T call onSuccess yet, wait until popup is dismissed
      setShowAnalyzePopup(true);
    } catch (error: any) {
      console.error('Error logging custom workout:', error);
      toast.error(error.message || t('components.logCustomWorkout.toast.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeWithArnold = () => {
    if (!completedWorkout) return;
    
    // Build workout data for Arnold - same pattern as gym workouts
    const customWorkoutData = {
      type: 'custom',
      title: completedWorkout.activityName,
      duration: completedWorkout.duration,
      difficulty: completedWorkout.difficulty,
      caloriesBurned: completedWorkout.caloriesBurned,
      notes: completedWorkout.notes,
      _ts: Date.now(),
    };
    
    const message = t('components.logCustomWorkout.coachMessage', { coach: coachName, activity: completedWorkout.activityName });
    
    try {
      localStorage.setItem('pendingWorkoutAnalysis', JSON.stringify(customWorkoutData));
    } catch (e) {
      console.warn('Failed to save custom workout analysis fallback:', e);
    }

    // Use React Router state - same reliable pattern as gym workouts
    navigate('/chat', {
      state: {
        workoutData: customWorkoutData,
        fromWorkout: true,
        message
      }
    });
  };

  const handleSkipAnalysis = () => {
    setShowAnalyzePopup(false);
    setCompletedWorkout(null);
    onSuccess?.(); // Call onSuccess now that popup is dismissed
    onClose();
  };

  // Show the analyze popup after workout completion
  if (showAnalyzePopup && completedWorkout) {
    return (
      <Dialog open={true} onOpenChange={handleSkipAnalysis}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              {t('components.logCustomWorkout.analyzeTitle', { coach: coachName })}
            </DialogTitle>
            <DialogDescription>
              {t('components.logCustomWorkout.analyzeDescription', { coach: coachName, activity: completedWorkout.activityName })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="text-muted-foreground">
                {t('components.logCustomWorkout.canAnalyze', { coach: coachName })}
              </p>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>{t('components.logCustomWorkout.canAnalyzeBenefits')}</li>
                <li>{t('components.logCustomWorkout.canAnalyzeRecovery')}</li>
                <li>{t('components.logCustomWorkout.canAnalyzeTips')}</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkipAnalysis}
                className="flex-1"
              >
                {t('components.logCustomWorkout.skip')}
              </Button>
              <Button
                onClick={handleAnalyzeWithArnold}
                className="flex-1 bg-gradient-primary text-black"
              >
                <Bot className="mr-2 h-4 w-4" />
                {t('components.logCustomWorkout.analyze')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('components.logCustomWorkout.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="activity">{t('components.logCustomWorkout.activityName')}</Label>
            <Input
              id="activity"
              placeholder={t('components.logCustomWorkout.activityPlaceholder')}
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">{t('components.logCustomWorkout.duration')}</Label>
            <Input
              id="duration"
              type="number"
              placeholder={t('components.logCustomWorkout.durationPlaceholder')}
              min="1"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="calories">{t('components.logCustomWorkout.caloriesBurnedOptional')}</Label>
            <Input
              id="calories"
              type="number"
              placeholder={t('components.logCustomWorkout.caloriesPlaceholder')}
              min="0"
              value={caloriesBurned}
              onChange={(e) => setCaloriesBurned(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="difficulty">{t('components.logCustomWorkout.difficulty')}</Label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger id="difficulty">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">{t('components.logCustomWorkout.difficultyEasy')}</SelectItem>
                <SelectItem value="medium">{t('components.logCustomWorkout.difficultyMedium')}</SelectItem>
                <SelectItem value="hard">{t('components.logCustomWorkout.difficultyHard')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('components.logCustomWorkout.notesOptional')}</Label>
            <Textarea
              id="notes"
              placeholder={t('components.logCustomWorkout.notesPlaceholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-primary text-black"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('components.logCustomWorkout.logging')}
                </>
              ) : (
                t('components.logCustomWorkout.logWorkout')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};